import { clamp } from '@fullstory/util/math';
import {
  FramePlayback,
  FramePlaybackHost,
  FsRootDocument,
  LoadResult,
} from '@fullstory/playback-web';
import {
  HighlightElementIdentifiers,
  NodePath,
  ObjectInfo,
  ObjectPathPattern,
  ObjectSelectorPattern,
  PlaybackContext,
  PlayPageOptions,
  PlayResult,
  RendererCursor,
  RendererHost,
  SeekResult,
  GroupByPropertyDisplay,
  ObjectCollection,
} from '@fullstory/renderer-shared';
import { WebPageActivity } from './pageactivity';
import { PageDesc, WebPageData } from '@fullstory/client-api';
import { AbstractRendererImpl } from '../abstract_renderer';
import { OffsetRect, Size } from '@fullstory/shared/viewport';
import { OverlayMode, Overlays } from '../overlays/overlay';
import { WebOverlays } from './weboverlay';
import { ActivityMapsHostClientStub } from '../page-insights/activitymaps';
import { ScrollMapsHostClientStub } from '../page-insights/scrollmaps';
import { CreateClient, Dispatcher } from '@fullstory/channel/proxy';
import { InspectorHostClientStub } from '../page-insights/inspector';
import { WebActivityMaps } from './page-insights/activitymaps';
import { WebScrollMaps } from './page-insights/scrollmaps';
import { WebInspector } from './page-insights/inspector';
import { RawEvent } from '@fullstory/recording-api';
import { ControlledIFrameElement } from '@fullstory/controlled-iframe';
import { Point } from '@fullstory/util/geometry';
import { ElementHighlighter } from './element-highlighting/element_highlighter';
import { findFirstInternalScrollNode } from '../scrollmapUtils';
import { queryElementsBySelector } from '../page-insights/document_lookup';
import { createPromise, wait } from '@fullstory/util/promise';
import { NativePlatform } from '@fullstory/proto/fs/api/segment';
import { computeFocusPaths, computePropertyValuesConfig } from '../page-insights/properties';

const oversizeViewportSlackPx = 100;

export class WebPageRenderer extends AbstractRendererImpl {
  private _pageStartTime = 0;

  private _pageActivity = new WebPageActivity();

  private readonly _windowSize: Readonly<Size>;
  private readonly _screenSize: Readonly<Size>;

  // Constants for mobile viewing desktop detection
  private static readonly LAYOUT_SIZE_THRESHOLD = 1.5;
  private static readonly VISUAL_VIEWPORT_THRESHOLD = 0.8;

  // The size of the layout viewport
  // TODO(bendean) Redundant with _windowSize?
  private _layout!: Size;

  // The recorded visual viewport, plus overscroll.
  private _visualViewport!: OffsetRect;

  // User activity location used to guess the viewport offset when we need to. See
  // getPlaybackViewport().
  private _userFocus!: Point;

  #framePlayback!: FramePlayback;

  private _elementHighlighter: ElementHighlighter;

  private _showingAbnormalViewportOrLayout = false;

  constructor(host: RendererHost, dispatcher: Dispatcher, ctx: PlaybackContext) {
    super(host, dispatcher, ctx);
    this._windowSize = {
      width: ctx.page.windowWidth,
      height: ctx.page.windowHeight,
    };
    this._screenSize = {
      width: ctx.page.screenWidth,
      height: ctx.page.screenHeight,
    };
    this._reset();
    this._elementHighlighter = new ElementHighlighter({
      activeRootDocFrame: () => this.activeRootDocFrame(),
      activeFsRootDocument: () => this.activeFsRootDocument(),
    });
    // Preload stylesheets right away and with high priority. Overfetching tends
    // to be rare with stylesheets, and they're expensive to fetch since they
    // need to be rewritten, so fetching them while we wait for the raw page to
    // load is worthwhile.
    for (const [href, entry] of Object.entries(this._ctx.page.resourceMap ?? {})) {
      if (entry.destination === 'style') {
        this._preloader.preloadWithPriority('high', href, 'style');
      }
    }
  }

  private _reset(): void {
    this._visualViewport = {
      offsetLeft: 0,
      offsetTop: 0,
      width: this._windowSize.width,
      height: this._windowSize.height,
    };

    this._layout = {
      width: this._windowSize.width,
      height: this._windowSize.height,
    };

    this._userFocus = {
      x: 0,
      y: 0,
    };

    // Reset temporary full page mode for page insights
    this._showingAbnormalViewportOrLayout = false;

    // Create the interface FramePlayback uses to communicate with the renderer.
    const host: FramePlaybackHost = {
      onEvent: this.onEvent,
      onResourceLoadStart: (href: string, destination: RequestDestination) => {
        this._host.onResourceEvent({
          kind: 'resourceLoadStart',
          href,
          destination,
        });
      },
      onResourceLoaded: (href: string, destination: RequestDestination, result: LoadResult) => {
        if (result === 'error') {
          this._host.onResourceEvent({
            kind: 'resourceLoadFailed',
            href,
            destination,
          });
        }
      },
      positionPointer: (x: number, y: number) => {
        this.overlays().positionPointer(x, y);
      },
      positionTouch: (touchid: number, x: number, y: number) => {
        this.overlays().positionTouch(touchid, x, y);
      },
      touchDown: (touchid: number, x: number, y: number) => {
        this.overlays().touchDown(touchid, x, y);
      },
      touchUp: (touchid: number) => {
        this.overlays().touchUp(touchid);
      },
      removeAllTouches: () => {
        this.overlays().removeAllTouches();
      },
      cursor: (cursorClass: string) => {
        this.overlays().view().cursor.setClass(cursorClass);
      },
      show: () => {
        // On Safari as of 5/2020, transformed elements are sometimes not
        // invalidated when an ancestor transitions its 'visibility' from
        // 'hidden' to 'visible'. This can cause playback to be blank.
        // See CH108483 for example sessions. To avoid this bug, we use
        // 'opacity' rather than 'visibility' to show or hide the playback
        // iframe.
        this.view().setCss('opacity', '1');
        console.debug('Playback content is now shown.');
      },
      hide: () => {
        // Some historical context: we used to switch the view to 'display:
        // none' when seeking, the idea being that we wouldn't need to pay for
        // the layout changes that the playback code was making until we
        // switched back to 'display: block'. That's true, but it comes at a
        // price: the underlying layout tree data structure that the browser
        // uses to track that information gets destroyed when switching to
        // 'display: none', and reconstructing it later has a significant
        // performance cost.
        //
        // We're also unable to measure anything that depends on layout when the
        // view is 'display: none', which means that we need to be careful to
        // recalculate any potentially bad measurements when we switch back to
        // 'display: block'. On top of that, switching to 'display: none' resets
        // CSS animations, inhibits font loading and rendering, and generally
        // contributes to FOUC and "flashing".
        //
        // By contrast, 'visibility: hidden' and 'opacity: 0' have none of these
        // downsides, and seem to perform just as well if not better in
        // practice. (See 'show()' above for details on why we use 'opacity' and
        // not 'visibility'.)
        this.view().setCss('opacity', '0');
        console.debug('Playback content is now hidden.');
      },
      updateRootFrameSize: this.#updateRootFrameSize,
      setUserFocus: this.#setUserFocus,
      playToTimeCommitted: (requestId: number | string) => {
        this._host.onPlayToTimeCommitted(requestId);
      },
      onObjectsMayHaveChanged: this.onObjectsMayHaveChanged,
    };

    this.#framePlayback = new FramePlayback({
      ctx: this._ctx,
      container: this.container(),
      host,
      preloader: this._preloader,
      orgId: this._ctx.page.orgId,
      windowSize: this._windowSize,
      screenSize: this._screenSize,
    });

    this.overlays().layoutController().setLayoutViewportSize(this._layout);
  }

  protected createOverlays(): Overlays {
    return new WebOverlays(this);
  }

  // Returns the container element which we're rendering into. This is owned by
  // WebPageRenderer.
  container(): HTMLDivElement {
    return this.playbackContainer().e as HTMLDivElement;
  }

  // Returns the <iframe> associated with the active root document. This is
  // owned by FramePlayback. Note that the active root document may change over
  // time, and there may not be an <iframe> associated with it if playback
  // hasn't started yet or we're playing in buffered mode.
  activeRootDocFrame(): ControlledIFrameElement | null {
    return this.#framePlayback.activeRootDocFrame();
  }

  activeFsRootDocument(): FsRootDocument | null {
    return this.#framePlayback.activeFsRootDocument();
  }

  async playToTime(pageOffset: number, requestId?: number | string): Promise<SeekResult> {
    const start = performance.now();
    await this.#framePlayback.playToTime(pageOffset, requestId);
    return {
      type: 'web',
      pageOffsetMs: pageOffset,
      totalTimeMs: performance.now() - start,
    };
  }

  async onPause(): Promise<void> {
    this.#framePlayback.onPause();
  }

  createInspector(): OverlayMode {
    const inspectorHost = CreateClient(
      this._dispatcher.Ch(),
      new InspectorHostClientStub(),
      'InspectorHost',
    );
    const inspectorImpl = new WebInspector(this, inspectorHost);
    this._dispatcher.RegisterService(inspectorImpl, 'Inspector');
    return inspectorImpl;
  }

  createActivityMaps(): OverlayMode {
    const activityMapsHost = CreateClient(
      this._dispatcher.Ch(),
      new ActivityMapsHostClientStub(),
      'ActivityMapsHost',
    );
    const activityMapsImpl = new WebActivityMaps(this, activityMapsHost, this._ctx);
    this._dispatcher.RegisterService(activityMapsImpl, 'ActivityMaps');
    return activityMapsImpl;
  }

  createScrollMaps(): OverlayMode {
    const scrollMapsHost = CreateClient(
      this._dispatcher.Ch(),
      new ScrollMapsHostClientStub(),
      'ScrollMapsHost',
    );
    const scrollMapsImpl = new WebScrollMaps(this, scrollMapsHost);
    this._dispatcher.RegisterService(scrollMapsImpl, 'ScrollMaps');
    return scrollMapsImpl;
  }

  override invalidateRendererGeometryCaches(): void {
    this.#framePlayback.invalidateRendererGeometryCaches();
  }

  override queryObjectsAtPoint(point: Point): ObjectInfo[] {
    return this.#framePlayback.queryPoint(point);
  }

  override queryPathPattern(pattern: ObjectPathPattern): ObjectInfo | undefined {
    const nodePath = NodePath.fromString(pattern.path);
    if (!nodePath) {
      return undefined;
    }
    return this.#framePlayback.queryPath(nodePath, pattern.opts);
  }

  override querySelectorPattern(pattern: ObjectSelectorPattern): ObjectInfo[] {
    if (pattern.platforms && !pattern.platforms.includes('web')) {
      return [];
    }
    return this.#framePlayback.querySelector(pattern.selector, pattern.opts);
  }

  override isDescendant(testPath: string, basePath: string): boolean {
    const testNodePath = NodePath.fromString(testPath);
    const baseNodePath = NodePath.fromString(basePath);
    if (!testNodePath || !baseNodePath) {
      return false;
    }
    return this.#framePlayback.isDescendant(testNodePath, baseNodePath);
  }

  computeDescendants(collection: ObjectCollection) {
    this.#framePlayback.computeDescendants(collection);
  }

  async openPage(page: PageDesc): Promise<void> {
    // Tear down playback data structures from the previous page, if any.
    // TODO(sethf): I don't think we actually reuse renderers between pages
    // anywhere. We should confirm that and then remove the relevant code and
    // just report an error if someone tries.
    await this.teardown();

    this._pageStartTime = page.StartTime;
  }

  async playPage(webPageData: WebPageData, options?: PlayPageOptions): Promise<PlayResult> {
    const start = performance.now();

    this.#updateRootFrameSize(
      {
        width: webPageData.Width,
        height: webPageData.Height,
      },
      {
        offsetLeft: 0,
        offsetTop: 0,
        width: webPageData.Width,
        height: webPageData.Height,
      },
    );

    // TODO(jordanhurt) - maybe remove this or subscribe to the raw event stream directly
    // to clean up a `skip(1)` call in PagePlayback
    this.#framePlayback.appendRawEvents(webPageData.RawEvents);

    // Find the location of the first insert and report it to the host.
    const firstInsertMs = this.#framePlayback.firstInsertMs();

    // TODO(jordanhurt): remove after the AppPlaybackFirstInsertWhenRefactor flag
    this._host.setFirstInsertWhen(firstInsertMs ?? 0);

    // Render the page at the initial timestamp and wait for everything to load
    // before giving the host the go-ahead to continue playback. This helps
    // avoid FOUC and FOUT, and because we do this before general preloading, it
    // allows the resources we need at the beginning of playback to load without
    // competition.
    await this.#framePlayback.playToInitialTimeStamp(options?.initialTimeStampMs);
    this._host.onResourceEvent({ kind: 'initialLoadComplete' });
    return {
      type: 'web',
      pageOffsetMs: options?.initialTimeStampMs ?? firstInsertMs,
      totalTimeMs: performance.now() - start,
    };
  }

  async appendTrailingEvents(trailingEvents: RawEvent[]): Promise<void> {
    if (trailingEvents) {
      this.#framePlayback.appendRawEvents(trailingEvents);
    }

    // Update page activity indicator.
    this._pageActivity.update(trailingEvents, this._pageStartTime);
    this._host.setPageActivity(this._pageActivity.activityState());
  }

  /**
   * Makes sure all <img/> tags have their images loaded. This also sets all lazy loading images
   * to eager.
   */
  async waitForImagesToLoad(maxWaitTimeMs: number): Promise<void> {
    const retryMs = 50;
    const doc = this.#framePlayback.activeRootDocFrame().contentDocument;

    const waitForImagesPromise = createPromise<void>();
    const images: Set<HTMLImageElement> = new Set(doc.querySelectorAll('img')?.values());
    const checkImagesRetry = setInterval(() => {
      images.forEach(imageElement => {
        if (imageElement.complete && imageElement.naturalHeight) {
          images.delete(imageElement);
        } else if (
          imageElement.getAttribute('hidden') ||
          window.getComputedStyle(imageElement).display === 'none'
        ) {
          // Don't worry about images that are never displayed
          images.delete(imageElement);
        } else if (imageElement.getAttribute('loading') === 'lazy') {
          imageElement.setAttribute('loading', 'eager');
        }
      });

      if (images.size === 0) {
        clearInterval(checkImagesRetry);
        waitForImagesPromise.resolve();
      }
    }, retryMs);

    await Promise.race([waitForImagesPromise.promise, wait(maxWaitTimeMs)]).finally(() => {
      clearInterval(checkImagesRetry);
    });
  }

  override async setCursorOverride(cursor: RendererCursor): Promise<void> {
    await this.#framePlayback.setCursorOverride(cursor);
  }

  override async setInteractive(interactive: boolean): Promise<void> {
    await this.#framePlayback.setInteractive(interactive);
  }

  #updateRootFrameSize = (layout: Readonly<Size>, visualViewport?: Readonly<OffsetRect>): void => {
    this._layout = {
      width: layout.width,
      height: layout.height,
    };

    this.overlays().layoutController().setLayoutViewportSize(this._layout);

    if (visualViewport) {
      if (this.isFullPageMode()) {
        this._visualViewport = {
          width: layout.width,
          height: layout.height,
          offsetLeft: 0,
          offsetTop: 0,
        };
      } else {
        this._visualViewport = {
          width: visualViewport.width,
          height: visualViewport.height,
          offsetLeft: visualViewport.offsetLeft,
          offsetTop: visualViewport.offsetTop,
        };
      }
    }

    this.rescale();
  };

  private rescale(disallowFloating = false) {
    const containerElem = this.playbackContainer().elem();

    if (this.isFullPageMode()) {
      // If we're in full page mode, the viewport should be set at 100% since we're mimicking the original session
      containerElem.style.setProperty('width', `100%`);
      containerElem.style.setProperty('height', `100%`);
      this.scalePlaybackFor(this._layout.width, this._layout.height, 1);
      this.view().viewport.setCssTransform(`scale(1, 1) translate(0px, 0px)`);

      return;
    }

    containerElem.style.setProperty('width', `${this._layout.width}px`);
    containerElem.style.setProperty('height', `${this._layout.height}px`);

    const { vp, floating } = this.getPlaybackViewport(disallowFloating);

    if (vp.height == 0 || vp.width == 0) {
      return;
    }

    // Resize playback div.scaled and calculate div.viewport's zoom amount.
    const aspectRatio = vp.width / vp.height;
    let frameWidth = this._layout.width || vp.width;
    let frameHeight = this._layout.height || vp.height;

    // If necessary, shrink the scaled div to match the viewport's aspect ratio.
    if (floating) {
      const adjustedFrameHeight = frameHeight / aspectRatio;
      const adjustedFrameWidth = frameWidth * aspectRatio;
      // Adjust the scaled div's aspect ratio, shrinking it as little as possible.
      if (adjustedFrameWidth < adjustedFrameHeight) {
        frameWidth = frameHeight * aspectRatio;
      } else {
        frameHeight = frameWidth / aspectRatio;
      }
    }

    const visualScale = frameWidth / vp.width;
    this.scalePlaybackFor(frameWidth, frameHeight, visualScale);

    this.view().viewport.setCssTransform(`
      scale(${visualScale}, ${visualScale})
      translate(${-vp.offsetLeft}px, ${-vp.offsetTop}px)`);

    // In floating mode, smooth out those transformations with a CSS transition.
    this.view().viewport.switchClass('floating', floating);
  }

  // Leave floating viewport mode when we enter page insights.
  // TODO(bendean|sethf after CH43186) Revisit this when we're emulating scrolling with translation.
  protected beforeEnteringPageInsights() {
    const isMobileViewingDesktop = this.isMobileViewingDesktopScenario();

    if (isMobileViewingDesktop) {
      // For mobile-viewing-desktop scenarios, temporarily switch to full page mode
      // so page insights can show the complete page content instead of just the small visual viewport
      this._showingAbnormalViewportOrLayout = true;
      this.rescale();
    } else {
      // Normal case: disable floating mode for page insights
      this.rescale(true);
    }
  }

  protected afterLeavingPageInsights() {
    // Clean up temporary full page mode if it was enabled
    if (this._showingAbnormalViewportOrLayout) {
      this._showingAbnormalViewportOrLayout = false;
    }

    this.rescale();
  }

  // Get viewport for replay, relative to the recorded page layout. Typically this the
  // visual viewport set by this.updateRootFrameSize. But when that visual viewport is
  // larger then the screen size, pick reasonable values for the size and offset.
  //
  // The visual viewport can be larger than the screen when an iframe is the recorded
  // page's top level, and the containing window handles scrolling (CH32384).
  private getPlaybackViewport(disallowFloating = false): { vp: OffsetRect; floating: boolean } {
    const floating =
      !disallowFloating &&
      this._ctx.flags.features.AppPlaybackFloatingViewportMode &&
      this._screenSize.width > 0 &&
      this._screenSize.height > 0 &&
      (this._screenSize.width + oversizeViewportSlackPx < this._visualViewport.width ||
        this._screenSize.height + oversizeViewportSlackPx < this._visualViewport.height);

    if (!floating) {
      // Ordinary case: Play back what we recorded.
      return {
        vp: this._visualViewport,
        floating: false,
      };
    }

    const { x, y } = this._userFocus;

    const width = Math.min(this._screenSize.width, this._visualViewport.width);
    const height = Math.min(this._screenSize.height, this._visualViewport.height);

    const maxLeft = Math.max(this._layout.width - width, 0);
    const maxTop = Math.max(this._layout.height - height, 0);

    // Compute offset relative to the layout viewport (ie visual scroll).
    let offsetLeft = x - width / 2;
    let offsetTop = y - height / 2;
    offsetLeft = clamp(offsetLeft, 0, maxLeft);
    offsetTop = clamp(offsetTop, 0, maxTop);

    return {
      vp: { width, height, offsetLeft, offsetTop },
      floating: true,
    };
  }

  #setUserFocus = ({ x, y }: Partial<Record<'x' | 'y', number>>): void => {
    // To avoid thrashing the offset, only update the user focus if the provided point is
    // outside, or almost outside, the current viewport.
    if (x !== undefined && Math.abs(x - this._userFocus.x) > (0.9 * this._screenSize.width) / 2) {
      this._userFocus.x = x;
    }
    if (y !== undefined && Math.abs(y - this._userFocus.y) > (0.9 * this._screenSize.height) / 2) {
      this._userFocus.y = y;
    }
    this.rescale();
  };

  async teardown(): Promise<void> {
    await this.#framePlayback.teardown();
    this._reset();
  }

  scrollToElement(
    elementSelector: string,
    propertyValue: string | undefined,
    focusSelectors: string[],
    groupByProperty: GroupByPropertyDisplay | undefined,
  ) {
    const focusPaths = computeFocusPaths(focusSelectors, this);
    const propertyValuesConfig = computePropertyValuesConfig(focusPaths, groupByProperty, this);
    const foundElements = queryElementsBySelector(
      elementSelector,
      propertyValue,
      focusPaths,
      propertyValuesConfig,
      this.#framePlayback.activeRootDocFrame().contentDocument,
      this,
    );
    if (foundElements.length > 0) {
      foundElements[0].elements.e[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  highlightElements(highlightElementIdentifiers?: HighlightElementIdentifiers) {
    if (!highlightElementIdentifiers) {
      return this.clearHighlighter();
    }
    this._elementHighlighter.start();
    this._elementHighlighter.highlightByElementsIdentifiers(highlightElementIdentifiers);
  }

  isInternalScrollingPage(selectors: string[]): boolean {
    return !!findFirstInternalScrollNode(this.activeRootDocFrame()?.contentDocument, selectors);
  }

  zeroCurrentScrollPosition() {
    this.#framePlayback.zeroCurrentScrollPosition();
  }

  /**
   * Gets the full HTML content of the playback frame as a string, including the content of any nested iframes.
   * This method provides access to the serialized HTML content while respecting CORS limitations,
   * as it uses the direct reference to the iframe's contentDocument.
   *
   * @returns The full HTML content of the playback frame as a string, or null if not available
   */
  getPlaybackFrameHTML(): string | null {
    const rootDocFrame = this.activeRootDocFrame();
    if (!rootDocFrame?.contentDocument) {
      return null;
    }

    // Access the document
    const doc = rootDocFrame.contentDocument;

    // Clone the document to avoid modifying the live DOM
    const docClone = doc.cloneNode(true) as Document;

    // Process all iframe elements in the document and their nested iframes
    const processIframes = (document: Document, liveDoc: Document) => {
      // Find all iframes in this document
      const iframes = document.querySelectorAll('iframe');

      for (const iframe of iframes) {
        try {
          const iframeElement = iframe as HTMLIFrameElement;
          const src = iframeElement.getAttribute('src');
          if (!src) continue;

          // Find the matching iframe in the live document
          const liveIframe = liveDoc.querySelector(`iframe[src="${src}"]`) as HTMLIFrameElement;
          if (!liveIframe?.contentDocument) continue;

          // Create the container for iframe content
          const iframeContent = document.createElement('fs-iframe-content');
          iframeContent.setAttribute('data-src', src);

          // Process content (with potential nested iframes)
          const frameDoc = liveIframe.contentDocument;

          // Check for nested iframes
          if (frameDoc.querySelectorAll('iframe').length > 0) {
            // Set up temporary document for processing nested iframes
            const tempDoc = document.implementation.createHTMLDocument();
            const frameClone = frameDoc.documentElement.cloneNode(true) as HTMLElement;
            tempDoc.replaceChild(frameClone, tempDoc.documentElement);

            // Process any nested iframes recursively
            processIframes(tempDoc, frameDoc);
            iframeContent.innerHTML = tempDoc.documentElement.outerHTML;
          } else {
            // No nested iframes - use content directly
            iframeContent.innerHTML = frameDoc.documentElement.outerHTML;
          }

          // Replace the iframe with our content container
          iframeElement.parentNode?.replaceChild(iframeContent, iframeElement);
        } catch (error) {
          console.error('Error processing iframe:', error);
        }
      }
    };

    // Process iframes in the document
    processIframes(docClone, doc);

    // Return serialized HTML
    return new XMLSerializer().serializeToString(docClone);
  }

  private clearHighlighter() {
    this._elementHighlighter.stop();
    this._layout;
  }

  getNativePlatform(): NativePlatform {
    return NativePlatform.NATIVE_PLATFORM_WEB;
  }

  /**
   * Detects if this is a mobile device viewing a desktop/non-mobile site.
   * This happens when the screen size is significantly smaller than the layout size,
   * indicating a mobile device is viewing a site designed for larger screens.
   */
  private isMobileViewingDesktopScenario(): boolean {
    // Check if we have valid dimensions
    if (
      this._screenSize.width <= 0 ||
      this._screenSize.height <= 0 ||
      this._layout.width <= 0 ||
      this._layout.height <= 0
    ) {
      return false;
    }

    // Calculate ratios to determine if layout is significantly larger than screen
    const widthRatio = this._layout.width / this._screenSize.width;
    const heightRatio = this._layout.height / this._screenSize.height;

    // Consider it mobile-viewing-desktop if:
    // 1. Layout is significantly wider than screen (desktop site on mobile)
    // 2. OR layout is significantly taller than screen
    // 3. AND the visual viewport is much smaller than the layout (indicating zoomed/cropped view)
    const isLayoutMuchLarger =
      widthRatio > WebPageRenderer.LAYOUT_SIZE_THRESHOLD ||
      heightRatio > WebPageRenderer.LAYOUT_SIZE_THRESHOLD;

    const visualViewportToLayoutWidthRatio = this._visualViewport.width / this._layout.width;
    const visualViewportToLayoutHeightRatio = this._visualViewport.height / this._layout.height;
    const isVisualViewportSmall =
      visualViewportToLayoutWidthRatio < WebPageRenderer.VISUAL_VIEWPORT_THRESHOLD ||
      visualViewportToLayoutHeightRatio < WebPageRenderer.VISUAL_VIEWPORT_THRESHOLD;

    const result = isLayoutMuchLarger && isVisualViewportSmall;
    return result;
  }

  override isFullPageMode(): boolean {
    return super.isFullPageMode() || this._showingAbnormalViewportOrLayout;
  }
}
